import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Package, DollarSign, Users, TrendingUp, Loader2, Check, X } from "lucide-react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDashboard() {
  const { user, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [affiliates, setAffiliates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("all");

  useEffect(() => {
    if (!user) {
      navigate("/login");
      return;
    }
    if (!user.is_admin) {
      navigate("/account");
      toast.error("Admin access required");
      return;
    }
    fetchData();
  }, [user, navigate]);

  const fetchData = async () => {
    try {
      const [statsRes, ordersRes, affiliatesRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers: getAuthHeaders() }),
        axios.get(`${API}/admin/orders`, { headers: getAuthHeaders() }),
        axios.get(`${API}/admin/affiliates`, { headers: getAuthHeaders() })
      ]);
      setStats(statsRes.data);
      setOrders(ordersRes.data);
      setAffiliates(affiliatesRes.data);
    } catch (error) {
      console.error("Failed to fetch admin data:", error);
      toast.error("Failed to load admin data");
    } finally {
      setLoading(false);
    }
  };

  const updateOrderStatus = async (orderId, status) => {
    try {
      await axios.put(
        `${API}/admin/orders/${orderId}/status?status=${status}`,
        {},
        { headers: getAuthHeaders() }
      );
      toast.success("Order status updated");
      fetchData();
    } catch (error) {
      toast.error("Failed to update order status");
    }
  };

  const payAffiliate = async (affiliateId) => {
    try {
      await axios.put(
        `${API}/admin/affiliates/${affiliateId}/pay`,
        {},
        { headers: getAuthHeaders() }
      );
      toast.success("Affiliate marked as paid");
      fetchData();
    } catch (error) {
      toast.error("Failed to process payment");
    }
  };

  if (!user || !user.is_admin) return null;

  const filteredOrders = filterStatus === "all" 
    ? orders 
    : orders.filter(o => o.status === filterStatus);

  const getStatusColor = (status) => {
    switch (status) {
      case "confirmed":
        return "bg-green-100 text-green-800";
      case "shipped":
        return "bg-blue-100 text-blue-800";
      case "delivered":
        return "bg-purple-100 text-purple-800";
      case "cancelled":
        return "bg-red-100 text-red-800";
      default:
        return "bg-yellow-100 text-yellow-800";
    }
  };

  return (
    <div className="container-main section-spacing">
      <div className="mb-8">
        <h1 className="text-h2 mb-2">Admin Dashboard</h1>
        <p className="text-muted-foreground">
          Manage orders, affiliates, and view business metrics.
        </p>
      </div>

      {/* Stats */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="animate-pulse card-dashboard">
              <div className="h-4 bg-muted rounded w-1/2 mb-4" />
              <div className="h-8 bg-muted rounded w-1/3" />
            </div>
          ))}
        </div>
      ) : stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="card-dashboard" data-testid="stat-total-orders">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                <Package className="w-5 h-5 text-primary" />
              </div>
              <span className="text-muted-foreground">Total Orders</span>
            </div>
            <p className="text-3xl font-heading">{stats.total_orders}</p>
          </div>

          <div className="card-dashboard" data-testid="stat-revenue">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-green-600" />
              </div>
              <span className="text-muted-foreground">Total Revenue</span>
            </div>
            <p className="text-3xl font-heading text-green-600">${stats.total_revenue.toFixed(2)}</p>
          </div>

          <div className="card-dashboard" data-testid="stat-pending">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-yellow-600" />
              </div>
              <span className="text-muted-foreground">Pending Orders</span>
            </div>
            <p className="text-3xl font-heading text-yellow-600">{stats.pending_orders}</p>
          </div>

          <div className="card-dashboard" data-testid="stat-affiliates">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
              <span className="text-muted-foreground">Affiliates</span>
            </div>
            <p className="text-3xl font-heading text-blue-600">{stats.total_affiliates}</p>
          </div>
        </div>
      )}

      {/* Tabs */}
      <Tabs defaultValue="orders" className="w-full">
        <TabsList className="mb-6">
          <TabsTrigger value="orders" data-testid="tab-orders">Orders</TabsTrigger>
          <TabsTrigger value="affiliates" data-testid="tab-affiliates">Affiliates</TabsTrigger>
        </TabsList>

        {/* Orders Tab */}
        <TabsContent value="orders">
          <div className="card-dashboard">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-xl">Order Management</h2>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-[180px]" data-testid="order-filter">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Orders</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="confirmed">Confirmed</SelectItem>
                  <SelectItem value="shipped">Shipped</SelectItem>
                  <SelectItem value="delivered">Delivered</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : filteredOrders.length === 0 ? (
              <div className="text-center py-12">
                <Package className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No orders found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4 font-medium">Order ID</th>
                      <th className="text-left py-3 px-4 font-medium">Customer</th>
                      <th className="text-left py-3 px-4 font-medium">Items</th>
                      <th className="text-left py-3 px-4 font-medium">Total</th>
                      <th className="text-left py-3 px-4 font-medium">Status</th>
                      <th className="text-left py-3 px-4 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredOrders.map((order) => (
                      <tr key={order.id} className="border-b last:border-0">
                        <td className="py-3 px-4 font-mono text-sm">
                          #{order.id.slice(0, 8)}
                        </td>
                        <td className="py-3 px-4">
                          {order.user_email}
                        </td>
                        <td className="py-3 px-4">
                          {order.items.length} item{order.items.length > 1 ? "s" : ""}
                        </td>
                        <td className="py-3 px-4 font-medium">
                          ${order.total.toFixed(2)}
                        </td>
                        <td className="py-3 px-4">
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(order.status)}`}>
                            {order.status}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <Select
                            value={order.status}
                            onValueChange={(value) => updateOrderStatus(order.id, value)}
                          >
                            <SelectTrigger className="w-[140px]" data-testid={`order-status-${order.id}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="pending">Pending</SelectItem>
                              <SelectItem value="confirmed">Confirmed</SelectItem>
                              <SelectItem value="shipped">Shipped</SelectItem>
                              <SelectItem value="delivered">Delivered</SelectItem>
                              <SelectItem value="cancelled">Cancelled</SelectItem>
                            </SelectContent>
                          </Select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </TabsContent>

        {/* Affiliates Tab */}
        <TabsContent value="affiliates">
          <div className="card-dashboard">
            <h2 className="font-heading text-xl mb-6">Affiliate Management</h2>

            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : affiliates.length === 0 ? (
              <div className="text-center py-12">
                <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No affiliates yet</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4 font-medium">Name</th>
                      <th className="text-left py-3 px-4 font-medium">Email</th>
                      <th className="text-left py-3 px-4 font-medium">Code</th>
                      <th className="text-left py-3 px-4 font-medium">Referrals</th>
                      <th className="text-left py-3 px-4 font-medium">Total Earnings</th>
                      <th className="text-left py-3 px-4 font-medium">Pending</th>
                      <th className="text-left py-3 px-4 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {affiliates.map((affiliate) => (
                      <tr key={affiliate.id} className="border-b last:border-0">
                        <td className="py-3 px-4 font-medium">
                          {affiliate.name}
                        </td>
                        <td className="py-3 px-4">
                          {affiliate.email}
                        </td>
                        <td className="py-3 px-4 font-mono text-sm">
                          {affiliate.affiliate_code}
                        </td>
                        <td className="py-3 px-4">
                          {affiliate.total_referrals}
                        </td>
                        <td className="py-3 px-4 text-green-600 font-medium">
                          ${affiliate.total_earnings.toFixed(2)}
                        </td>
                        <td className="py-3 px-4 text-yellow-600 font-medium">
                          ${affiliate.pending_earnings.toFixed(2)}
                        </td>
                        <td className="py-3 px-4">
                          {affiliate.pending_earnings > 0 && (
                            <Button
                              size="sm"
                              onClick={() => payAffiliate(affiliate.id)}
                              className="bg-green-600 hover:bg-green-700"
                              data-testid={`pay-affiliate-${affiliate.id}`}
                            >
                              <Check className="w-4 h-4 mr-1" />
                              Mark Paid
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
